// Utility to collect browser/device metadata at order time without blocking legacy flows.
// All parsing happens in-browser so API payload stays lightweight and optional.

const DEVICE_CONTEXT_FALLBACK = Object.freeze({
  environment: "unknown",
  deviceType: "unknown",
  os: { name: "unknown", version: null },
  browser: { name: "unknown", version: null },
  device: { vendor: null, model: null },
  inApp: { isInApp: false, appName: null, version: null },
  userAgent: "",
  screen: { width: null, height: null, pixelRatio: null },
  connection: { type: null, downlink: null },
  platformHints: { platform: null, languages: [], isTouch: false },
  rawPayload: {},
});

const IN_APP_SIGNATURES = [
  { name: "Facebook", regex: /FBAV/i, version: /FBAV\/([\d\.]+)/i },
  { name: "Instagram", regex: /Instagram/i, version: /Instagram\s([\d\.]+)/i },
  { name: "Messenger", regex: /FBAN\/Messenger/i, version: /FBAV\/([\d\.]+)/i },
  { name: "WhatsApp", regex: /WhatsApp/i, version: /WhatsApp\/([\d\.]+)/i },
  { name: "Twitter", regex: /Twitter/i, version: /Twitter\/([\d\.]+)/i },
  { name: "LinkedIn", regex: /LinkedInApp/i, version: /LinkedInApp\/([\d\.]+)/i },
  { name: "Snapchat", regex: /Snapchat/i, version: /Snapchat\/([\d\.]+)/i },
  { name: "Telegram", regex: /Telegram/i, version: /Telegram[\w\/ ]+([\d\.]+)/i },
  { name: "Pinterest", regex: /Pinterest/i, version: /Pinterest\/([\d\.]+)/i },
];

const BROWSER_SIGNATURES = [
  { name: "Edge", regex: /Edg(?:e|A|iOS)?\/(\d+[\.\d]+)/i },
  { name: "Chrome", regex: /Chrome\/(\d+[\.\d]+)/i },
  { name: "Firefox", regex: /Firefox\/(\d+[\.\d]+)/i },
  { name: "Safari", regex: /Version\/(\d+[\.\d]+).*Safari/i },
  { name: "Opera", regex: /OPR\/(\d+[\.\d]+)/i },
  { name: "Brave", regex: /Brave\/(\d+[\.\d]+)/i },
  { name: "Samsung Internet", regex: /SamsungBrowser\/(\d+[\.\d]+)/i },
  { name: "DuckDuckGo", regex: /DuckDuckGo\/(\d+[\.\d]+)/i },
];

const ANDROID_MODEL_REGEX = /;\s?([A-Z]{2,}-[A-Z0-9]{2,}|[A-Z][A-Za-z0-9\s-]+)\sBuild/i;

const extractMatch = (regex, text) => {
  if (!regex || !text) return null;
  const match = text.match(regex);
  return match && match[1] ? match[1] : null;
};

const detectInApp = (ua) => {
  for (const signature of IN_APP_SIGNATURES) {
    if (signature.regex.test(ua)) {
      return {
        isInApp: true,
        appName: signature.name,
        version: extractMatch(signature.version, ua),
      };
    }
  }

  if (/FBAN/i.test(ua)) {
    return { isInApp: true, appName: "Facebook", version: extractMatch(/FBAV\/([\d\.]+)/i, ua) };
  }

  if (/wv\)/i.test(ua) || /WebView/i.test(ua)) {
    return { isInApp: true, appName: "Android WebView", version: extractMatch(/Version\/(\d+[\.\d]+)/i, ua) };
  }

  return { isInApp: false, appName: null, version: null };
};

const detectOS = (ua, platform) => {
  if (/Windows NT 10\.0/i.test(ua)) return { name: "Windows", version: "10" };
  if (/Windows NT 11\.0/i.test(ua)) return { name: "Windows", version: "11" };
  if (/Mac OS X (\d+[_.]\d+)/i.test(ua)) return { name: "macOS", version: extractMatch(/Mac OS X (\d+[_.]\d+)/i, ua)?.replace("_", ".") || null };
  if (/Android\s([\d.]+)/i.test(ua)) return { name: "Android", version: extractMatch(/Android\s([\d.]+)/i, ua) };
  if (/iP(hone|ad|od);/i.test(ua)) {
    const version = extractMatch(/OS\s([\d_]+)/i, ua);
    return { name: "iOS", version: version ? version.replace(/_/g, ".") : null };
  }
  if (/Linux/i.test(platform)) return { name: "Linux", version: null };
  return { name: "unknown", version: null };
};

const detectDeviceType = (ua) => {
  if (/iPad/i.test(ua)) return "tablet";
  if (/Tablet|PlayBook|Silk/i.test(ua)) return "tablet";
  if (/Mobi|Android|iPhone|iPod/i.test(ua)) return "phone";
  if (/TV|SmartTV|HbbTV/i.test(ua)) return "tv";
  return "desktop";
};

const detectBrowser = (ua) => {
  for (const signature of BROWSER_SIGNATURES) {
    const version = extractMatch(signature.regex, ua);
    if (version) {
      return { name: signature.name, version };
    }
  }

  if (/CriOS\/(\d+[\.\d]+)/i.test(ua)) {
    return { name: "Chrome iOS", version: extractMatch(/CriOS\/(\d+[\.\d]+)/i, ua) };
  }

  if (/FxiOS\/(\d+[\.\d]+)/i.test(ua)) {
    return { name: "Firefox iOS", version: extractMatch(/FxiOS\/(\d+[\.\d]+)/i, ua) };
  }

  return { name: "unknown", version: null };
};

const detectDeviceVendorModel = (ua) => {
  if (/iPhone/i.test(ua)) return { vendor: "Apple", model: "iPhone" };
  if (/iPad/i.test(ua)) return { vendor: "Apple", model: "iPad" };

  const model = extractMatch(ANDROID_MODEL_REGEX, ua);
  if (model) {
    return { vendor: null, model: model.trim() };
  }

  return { vendor: null, model: null };
};

const detectEnvironment = (ua) => {
  if (/wv\)/i.test(ua) || /FBAN|FBAV|Instagram|Twitter|Snapchat|LinkedInApp|WhatsApp/i.test(ua)) {
    return "in-app";
  }
  if (/Mobi|Android|iPhone|iPad|iPod/i.test(ua)) {
    return "mobile-web";
  }
  return "web";
};

const collectScreen = () => {
  if (typeof window === "undefined" || !window.screen) {
    return { width: null, height: null, pixelRatio: null };
  }
  return {
    width: window.screen.width || null,
    height: window.screen.height || null,
    pixelRatio: window.devicePixelRatio || null,
  };
};

const collectConnection = (navigatorRef) => {
  const connection =
    navigatorRef.connection || navigatorRef.mozConnection || navigatorRef.webkitConnection || null;
  if (!connection) {
    return { type: null, downlink: null };
  }
  return {
    type: connection.effectiveType || connection.type || null,
    downlink: typeof connection.downlink === "number" ? connection.downlink : null,
  };
};

/**
 * Collects a snapshot of the current device/browser environment.
 * Usage example (before placing an order):
 *
 *   import { getDeviceContext } from "../utils/deviceContext";
 *   const payload = getDeviceContext();
 *   await api.post("/orders", { ...orderData, deviceContext: payload });
 *
 * Function never throws; it returns a static fallback on unsupported platforms.
 */
export const getDeviceContext = () => {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return DEVICE_CONTEXT_FALLBACK;
  }

  const navigatorRef = window.navigator || {};
  const userAgent = navigatorRef.userAgent || "";
  const platform = navigatorRef.platform || "";
  const languages = Array.isArray(navigatorRef.languages) ? navigatorRef.languages : [];
  const isTouch = typeof window.matchMedia === "function"
    ? window.matchMedia("(pointer: coarse)").matches
    : typeof navigatorRef.maxTouchPoints === "number" && navigatorRef.maxTouchPoints > 0;

  const environment = detectEnvironment(userAgent);
  const os = detectOS(userAgent, platform);
  const deviceType = detectDeviceType(userAgent);
  const browser = detectBrowser(userAgent);
  const device = detectDeviceVendorModel(userAgent);
  const inApp = detectInApp(userAgent);
  const screen = collectScreen();
  const connection = collectConnection(navigatorRef);

  const rawPayload = {
    userAgent,
    platform,
    vendor: navigatorRef.vendor || null,
    language: navigatorRef.language || null,
    languages,
    hardwareConcurrency: navigatorRef.hardwareConcurrency || null,
    maxTouchPoints: navigatorRef.maxTouchPoints || null,
    standalone: navigatorRef.standalone || null,
  };

  return {
    environment,
    deviceType,
    os,
    browser,
    device,
    inApp,
    userAgent,
    screen,
    connection,
    platformHints: { platform, languages, isTouch },
    rawPayload,
  };
};

export default getDeviceContext;
